import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, finalize, map, of, startWith } from 'rxjs';
import { HrAlertService, HrHttpService, MrfLocationFilters } from '../hr-http.service';
import {
  MrfDashboardStateService,
  MrfFilterDialogState,
} from '../mrf-dashboard-state.service';

type MrfFilterMode = 'default' | 'customize';
type MrfFilterByField = 'los' | 'state' | 'city' | 'zone' | 'subZone' | 'deanName' | 'campus';

export interface MrfFilterDialogData {
  mode: MrfFilterMode;
  /** When true, do not read/write the MRF dashboard filter snapshot. */
  skipDashboardState?: boolean;
  savedState?: MrfFilterDialogState | null;
}

interface MrfFilterByOption {
  value: MrfFilterByField;
  label: string;
}

interface CustomizeFilterRow {
  id: number;
  filterBy: MrfFilterByField | null;
  options: any[];
  selectedValue: any | null;
  searchControl: FormControl<string>;
  filteredOptions$: Observable<any[]>;
  loading: boolean;
}

@Component({
  selector: 'app-mrf-filter-dialog',
  templateUrl: './mrf-filter-dialog.component.html',
  styleUrls: ['./mrf-filter-dialog.component.scss'],
})
export class MrfFilterDialogComponent implements OnInit {
  private readonly filterNoDataMessage = 'No data available. Please change previous selection.';

  private readonly filterHierarchy: MrfFilterByField[] = [
    'los',
    'state',
    'city',
    'zone',
    'subZone',
    'deanName',
    'campus',
  ];

  filterMode: MrfFilterMode = 'default';

  readonly filterByOptions: MrfFilterByOption[] = [
    { value: 'los', label: 'Los' },
    { value: 'state', label: 'State' },
    { value: 'city', label: 'City' },
    { value: 'zone', label: 'Zone' },
    { value: 'subZone', label: 'Sub Zone' },
    { value: 'deanName', label: 'Dean Name' },
    { value: 'campus', label: 'Campus' },
  ];

  customizeRows: CustomizeFilterRow[] = [];
  private customizeRowId = 0;

  losFilter = new FormControl('', { nonNullable: true });
  stateFilter = new FormControl('', { nonNullable: true });
  cityFilter = new FormControl('', { nonNullable: true });
  zoneFilter = new FormControl('', { nonNullable: true });
  subZoneFilter = new FormControl('', { nonNullable: true });
  deanFilter = new FormControl('', { nonNullable: true });
  campusFilter = new FormControl('', { nonNullable: true });

  selectedLos: any = null;
  selectedState: any = null;
  selectedCity: any = null;
  selectedZone: any = null;
  selectedSubZone: any = null;
  selectedDean: any = null;
  selectedCampus: any = null;

  losOptions: any;
  stateOptions: any;
  cityOptions: any;
  zoneOptions: any;
  subZoneOptions: any;
  deanOptions: any;
  campusOptions: any;

  losList: any[] = [];
  states: any[] = [];
  cities: any[] = [];
  zones: any[] = [];
  subZones: any[] = [];
  deans: any[] = [];
  campuses: any[] = [];

  private appliedLos = '';
  private appliedStateId: number | null = null;
  private appliedCityId: number | null = null;
  private appliedZoneName = '';
  private appliedSubZone = '';
  private appliedDean = '';

  constructor(
    private dialogRef: MatDialogRef<MrfFilterDialogComponent>,
    private hrHttp: HrHttpService,
    private hrAlert: HrAlertService,
    private spinner: NgxSpinnerService,
    private dashboardState: MrfDashboardStateService,
    @Inject(MAT_DIALOG_DATA) private dialogData: MrfFilterDialogData
  ) {}

  ngOnInit(): void {
    this.filterMode = this.dialogData?.mode === 'customize' ? 'customize' : 'default';
    const savedState = this.dialogData?.savedState
      ?? (this.usesDashboardState() ? this.dashboardState.filterDialogState : null);
    if (savedState) {
      this.restoreFilterDialogState(savedState);
      return;
    }
    if (this.isDefaultMode) {
      this.loadLos();
      return;
    }
    this.initCustomizeRows();
  }

  get dialogTitle(): string {
    return this.isCustomizeMode ? 'Customize Filter' : 'Filter';
  }

  get isDefaultMode(): boolean {
    return this.filterMode === 'default';
  }

  get isCustomizeMode(): boolean {
    return this.filterMode === 'customize';
  }

  initCustomizeRows(): void {
    this.customizeRows = [this.createCustomizeRow()];
  }

  canAddCustomizeRow(row: CustomizeFilterRow): boolean {
    if (this.customizeRows.length >= this.filterByOptions.length) {
      return false;
    }
    return !!(row.filterBy && row.selectedValue);
  }

  addCustomizeRow(row: CustomizeFilterRow): void {
    if (!this.canAddCustomizeRow(row)) {
      return;
    }
    this.customizeRows = [...this.customizeRows, this.createCustomizeRow()];
  }

  removeCustomizeRow(rowId: number): void {
    if (this.customizeRows.length <= 1) {
      return;
    }
    const removedIndex = this.customizeRows.findIndex((row) => row.id === rowId);
    if (removedIndex === -1) {
      return;
    }
    this.customizeRows = this.customizeRows.filter((row) => row.id !== rowId);
    for (let i = removedIndex; i < this.customizeRows.length; i++) {
      this.resetCustomizeRowSelection(this.customizeRows[i]);
    }
  }

  getAvailableFilterByOptions(currentRow: CustomizeFilterRow): MrfFilterByOption[] {
    const used = new Set(
      this.customizeRows
        .filter((row) => row.id !== currentRow.id && row.filterBy)
        .map((row) => row.filterBy as MrfFilterByField)
    );
    return this.filterByOptions.filter((option) => !used.has(option.value));
  }

  onCustomizeFilterByChange(row: CustomizeFilterRow, filterBy: MrfFilterByField | null): void {
    const previousFilterBy = row.filterBy;
    row.filterBy = filterBy;
    row.selectedValue = null;
    row.options = [];
    row.searchControl.setValue('');
    this.bindCustomizeRowOptions(row);

    if (previousFilterBy) {
      this.clearCustomizeDependents(row);
    }

    if (!filterBy) {
      return;
    }

    this.loadCustomizeRowOptions(row);
  }

  onCustomizeValueChange(row: CustomizeFilterRow, value: any | null): void {
    if (!row.filterBy) {
      row.selectedValue = value;
      return;
    }

    const previousValue = row.selectedValue;
    row.selectedValue = value;

    if (!value || !this.isSameFilterValue(row.filterBy, previousValue, value)) {
      this.clearCustomizeDependents(row);
    }

    if (!value) {
      return;
    }

    if (!this.isSameFilterValue(row.filterBy, previousValue, value)) {
      this.reloadCustomizeDependents(row);
    }
  }

  getCustomizeOptionLabel(filterBy: MrfFilterByField | null, item: any): string {
    if (!filterBy || !item) {
      return '';
    }
    return this.getItemLabel(item, filterBy);
  }

  compareCustomizeValues = (a: any, b: any): boolean => {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    return (
      a.stateId === b.stateId &&
      a.cityId === b.cityId &&
      a.zoneName === b.zoneName &&
      a.campusId === b.campusId &&
      this.getLosValue(a) === this.getLosValue(b) &&
      this.getSubZoneValue(a) === this.getSubZoneValue(b) &&
      this.getDeanValue(a) === this.getDeanValue(b)
    );
  };

  compareLosValues = (a: any, b: any): boolean =>
    !!a && !!b && this.getLosValue(a) === this.getLosValue(b);

  compareStateValues = (a: any, b: any): boolean =>
    !!a && !!b && a.stateId === b.stateId;

  compareCityValues = (a: any, b: any): boolean =>
    !!a && !!b && a.cityId === b.cityId;

  compareZoneValues = (a: any, b: any): boolean =>
    !!a && !!b && a.zoneName === b.zoneName;

  compareSubZoneValues = (a: any, b: any): boolean =>
    !!a && !!b && this.getSubZoneValue(a) === this.getSubZoneValue(b);

  compareDeanValues = (a: any, b: any): boolean =>
    !!a && !!b && this.getDeanValue(a) === this.getDeanValue(b);

  compareCampusValues = (a: any, b: any): boolean =>
    !!a && !!b && a.campusId === b.campusId;

  getCustomizeDisplayValue(row: CustomizeFilterRow): string {
    if (!row.selectedValue || !row.filterBy) {
      return '';
    }
    return this.getItemLabel(row.selectedValue, row.filterBy);
  }

  loadLos(): void {
    this.subscribeFilterApi(this.hrHttp.getEmployeeLos(), (list) => {
      this.losList = list;
      this.losOptions = this.losFilter.valueChanges.pipe(
          startWith(''),
        map((value: string) => this.filterList(value, this.losList, ['name', 'location', 'los', 'losName']))
      );
    }, { showSpinner: true });
  }

  onLosChange(item: any | null): void {
    if (!item) {
      this.appliedLos = '';
      this.clearLosDependents();
      return;
    }

    const los = this.getLosValue(item);
    if (!los) {
      this.appliedLos = '';
      this.clearLosDependents();
      return;
    }

    if (los === this.appliedLos && this.states.length) {
      return;
    }

    this.appliedLos = los;
    this.clearLosDependents();
    this.subscribeFilterApi(
      this.hrHttp.getLosStates(los),
      (list) => {
        this.states = list;
        this.bindStateOptions();
      },
      { showSpinner: true, onNoData: () => this.clearLosDependents() }
    );
  }

  onDefaultStateChange(state: any | null): void {
    if (!state) {
      this.appliedStateId = null;
      this.clearStateDependents();
      return;
    }

    const stateId = state.stateId ?? null;
    if (stateId === this.appliedStateId && this.cities.length) {
      return;
    }

    this.appliedStateId = stateId;
    this.clearStateDependents();
    this.subscribeFilterApi(
      this.hrHttp.getStateCities(state.stateId),
      (list) => {
        this.cities = list;
        this.bindCityOptions();
      },
      { showSpinner: true, onNoData: () => this.clearStateDependents() }
    );
  }

  onDefaultCityChange(city: any | null): void {
    if (!city) {
      this.appliedCityId = null;
      this.clearCityDependents();
      return;
    }

    const cityId = city.cityId ?? null;
    if (cityId === this.appliedCityId && this.zones.length) {
      return;
    }

    this.appliedCityId = cityId;
    this.clearCityDependents();
    this.subscribeFilterApi(
      this.hrHttp.getCityZones(city.cityId),
      (list) => {
        this.zones = list;
        this.bindZoneOptions();
      },
      { showSpinner: true, onNoData: () => this.clearCityDependents() }
    );
  }

  onDefaultZoneChange(zone: any | null): void {
    if (!zone) {
      this.appliedZoneName = '';
      this.clearZoneDependents();
      return;
    }

    const zoneName = String(zone?.zoneName ?? '').trim();
    if (!zoneName) {
      this.appliedZoneName = '';
      this.clearZoneDependents();
      return;
    }

    if (zoneName === this.appliedZoneName && this.subZones.length) {
      return;
    }

    this.appliedZoneName = zoneName;
    this.clearZoneDependents();
    this.subscribeFilterApi(
      this.hrHttp.getZoneSubZones(zoneName),
      (list) => {
        this.subZones = list;
        this.subZoneOptions = this.subZoneFilter.valueChanges.pipe(
          startWith(''),
          map((value: string) =>
            this.filterList(value, this.subZones, ['name', 'subZone', 'subZoneName'])
          )
        );
      },
      { showSpinner: true, onNoData: () => this.clearZoneDependents() }
    );
  }

  onSubZoneChange(subZone: any | null): void {
    if (!subZone) {
      this.appliedSubZone = '';
      this.clearSubZoneDependents();
      return;
    }

    const subZoneName = this.getSubZoneValue(subZone);
    if (!subZoneName) {
      this.appliedSubZone = '';
      this.clearSubZoneDependents();
      return;
    }

    if (subZoneName === this.appliedSubZone && this.deans.length) {
      return;
    }

    this.appliedSubZone = subZoneName;
    this.clearSubZoneDependents();
    this.subscribeFilterApi(
      this.hrHttp.getSubZoneDeans(subZoneName),
      (list) => {
        this.deans = list;
        this.deanOptions = this.deanFilter.valueChanges.pipe(
          startWith(''),
          map((value: string) =>
            this.filterList(value, this.deans, ['name', 'deanName', 'principalDeanName'])
          )
        );
      },
      { showSpinner: true, onNoData: () => this.clearSubZoneDependents() }
    );
  }

  onDeanChange(dean: any | null): void {
    if (!dean) {
      this.appliedDean = '';
      this.clearDeanDependents();
      return;
    }

    const deanName = this.getDeanValue(dean);
    if (!deanName) {
      this.appliedDean = '';
      this.clearDeanDependents();
      return;
    }

    if (deanName === this.appliedDean && this.campuses.length) {
      return;
    }

    this.appliedDean = deanName;
    this.clearDeanDependents();
    this.subscribeFilterApi(
      this.hrHttp.getDeanCampuses(deanName),
      (list) => {
        this.campuses = list;
        this.bindCampusOptions();
      },
      { showSpinner: true, onNoData: () => this.clearDeanDependents() }
    );
  }

  onCampusChange(_campus: any | null): void {
    // Campus is the last level; nothing below to clear.
  }

  close(): void {
    this.persistFilterDialogState();
    this.dialogRef.close();
  }

  search(): void {
    if (this.isDefaultMode) {
      this.searchDefault();
      return;
    }
    this.searchCustomize();
  }

  clearCustomizeFilters(): void {
    if (!this.isCustomizeMode) {
      return;
    }
    this.customizeRowId = 0;
    this.initCustomizeRows();
  }

  resetCustomizeFilters(): void {
    if (!this.isCustomizeMode) {
      return;
    }
    this.customizeRowId = 0;
    this.initCustomizeRows();
    if (this.usesDashboardState()) {
      this.dashboardState.setFilterDialogState(null);
    }
    this.dialogRef.close({
      status: 'reset',
      filterMode: this.filterMode,
      dialogState: null,
    });
  }

  private searchDefault(): void {
    const hasSelection = !!(
      this.selectedLos ||
      this.selectedState ||
      this.selectedCity ||
      this.selectedZone ||
      this.selectedSubZone ||
      this.selectedDean ||
      this.selectedCampus
    );

    if (!hasSelection) {
      this.hrAlert.show('Please select at least one filter.', 'Warning');
      return;
    }

    const filters: MrfLocationFilters = {
      los: this.getLosValue(this.selectedLos) || null,
      stateId: this.selectedState?.stateId ?? null,
      cityId: this.selectedCity?.cityId ?? null,
      zoneName: this.selectedZone?.zoneName ?? null,
      subZone: this.getSubZoneValue(this.selectedSubZone) || null,
      deanName: this.getDeanValue(this.selectedDean) || null,
      campusId: this.selectedCampus?.campusId ?? null,
    };

    this.persistFilterDialogState();

    this.dialogRef.close({
      status: 'search',
      filterMode: this.filterMode,
      primaryFilterBy: null,
      los: this.selectedLos,
      state: this.selectedState,
      city: this.selectedCity,
      zone: this.selectedZone,
      subZone: this.selectedSubZone,
      dean: this.selectedDean,
      campus: this.selectedCampus,
      filters,
    });
  }

  private searchCustomize(): void {
    const activeRows = this.customizeRows.filter((row) => row.filterBy && row.selectedValue);

    if (!activeRows.length) {
      this.hrAlert.show('Please select at least one filter.', 'Warning');
      return;
    }

    const filters: MrfLocationFilters = {};
    for (const row of activeRows) {
      this.applyCustomizeRowToFilters(row, filters);
    }

    const primaryFilterBy = this.getCustomizePrimaryFilterBy(activeRows);

    this.persistFilterDialogState();

    this.dialogRef.close({
      status: 'search',
      filterMode: this.filterMode,
      primaryFilterBy,
      customizeRows: activeRows.map((row) => ({
        filterBy: row.filterBy,
        value: row.selectedValue,
      })),
      filters,
      dialogState: this.buildFilterDialogState(),
    });
  }

  private getCustomizePrimaryFilterBy(rows: CustomizeFilterRow[]): MrfFilterByField {
    let primary = rows[0]?.filterBy ?? 'los';
    let primaryIndex = this.filterHierarchy.indexOf(primary);

    for (const row of rows) {
      if (!row.filterBy) {
        continue;
      }
      const rowIndex = this.filterHierarchy.indexOf(row.filterBy);
      if (rowIndex > primaryIndex) {
        primary = row.filterBy;
        primaryIndex = rowIndex;
      }
    }

    return primary;
  }

  private applyCustomizeRowToFilters(row: CustomizeFilterRow, filters: MrfLocationFilters): void {
    if (!row.filterBy || !row.selectedValue) {
      return;
    }

    switch (row.filterBy) {
      case 'los':
        filters.los = this.getLosValue(row.selectedValue) || null;
        break;
      case 'state':
        filters.stateId = row.selectedValue?.stateId ?? null;
        break;
      case 'city':
        filters.cityId = row.selectedValue?.cityId ?? null;
        break;
      case 'zone':
        filters.zoneName = row.selectedValue?.zoneName ?? null;
        break;
      case 'subZone':
        filters.subZone = this.getSubZoneValue(row.selectedValue) || null;
        break;
      case 'deanName':
        filters.deanName = this.getDeanValue(row.selectedValue) || null;
        break;
      case 'campus':
        filters.campusId = row.selectedValue?.campusId ?? null;
        break;
    }
  }

  private createCustomizeRow(): CustomizeFilterRow {
    const searchControl = new FormControl('', { nonNullable: true });
    const row: CustomizeFilterRow = {
      id: ++this.customizeRowId,
      filterBy: null,
      options: [],
      selectedValue: null,
      searchControl,
      filteredOptions$: of([]),
      loading: false,
    };
    this.bindCustomizeRowOptions(row);
    return row;
  }

  private bindCustomizeRowOptions(row: CustomizeFilterRow): void {
    row.filteredOptions$ = row.searchControl.valueChanges.pipe(
      startWith(''),
      map((value: string) => {
        if (!row.filterBy) {
          return [];
        }
        return this.filterList(value, row.options, this.getDisplayFields(row.filterBy));
      })
    );
  }

  private loadCustomizeRowOptions(row: CustomizeFilterRow): void {
    const request$ = this.getCustomizeOptionsRequest(row);
    if (!request$) {
      row.options = [];
      this.bindCustomizeRowOptions(row);
      return;
    }

    row.loading = true;
    request$
      .pipe(finalize(() => (row.loading = false)))
      .subscribe({
        next: (res: any) => {
          if (this.isFilterNoContent(res)) {
            this.showFilterNoDataAlert();
            row.options = [];
            row.selectedValue = null;
            this.bindCustomizeRowOptions(row);
            return;
          }

          const list = this.extractList(res);
          if (!list.length) {
            this.showFilterNoDataAlert();
            row.selectedValue = null;
          }
          row.options = list;
          this.bindCustomizeRowOptions(row);
        },
        error: (err) => this.handleFilterApiError(err, () => {
          row.options = [];
          row.selectedValue = null;
          this.bindCustomizeRowOptions(row);
        }),
      });
  }

  private getCustomizeOptionsRequest(row: CustomizeFilterRow): Observable<any> | null {
    if (!row.filterBy) {
      return null;
    }
    if (!this.validateCustomizeParentFilters(row)) {
      return null;
    }
    const parentFilters = this.buildCustomizeParentFilters(row);
    return this.hrHttp.getMrfCustomizeFilterOptions(parentFilters, row.filterBy);
  }

  private buildCustomizeParentFilters(row: CustomizeFilterRow): MrfLocationFilters {
    const filters: MrfLocationFilters = {};
    const rowIndex = this.customizeRows.findIndex((item) => item.id === row.id);
    if (rowIndex <= 0) {
      return filters;
    }

    for (let i = 0; i < rowIndex; i++) {
      const priorRow = this.customizeRows[i];
      if (priorRow.filterBy && priorRow.selectedValue) {
        this.applyCustomizeRowToFilters(priorRow, filters);
      }
    }

    return filters;
  }

  private validateCustomizeParentFilters(row: CustomizeFilterRow): boolean {
    const rowIndex = this.customizeRows.findIndex((item) => item.id === row.id);
    if (rowIndex <= 0) {
      return true;
    }

    const hasPriorSelection = this.customizeRows
      .slice(0, rowIndex)
      .some((priorRow) => priorRow.filterBy && priorRow.selectedValue);

    if (!hasPriorSelection) {
      this.hrAlert.show('Select a value in the previous filter row to load options.', 'Warning');
      return false;
    }

    return true;
  }

  private getItemLabel(item: any, filterBy: MrfFilterByField): string {
    const fields = this.getDisplayFields(filterBy);
    for (const field of fields) {
      const value = item?.[field];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
    return '';
  }

  private getDisplayFields(filterBy: MrfFilterByField): string[] {
    switch (filterBy) {
      case 'los':
        return ['name', 'location', 'los', 'losName'];
      case 'state':
        return ['name', 'stateName'];
      case 'city':
        return ['name', 'cityName'];
      case 'zone':
        return ['name', 'zoneName', 'zone'];
      case 'subZone':
        return ['name', 'subZone', 'subZoneName'];
      case 'deanName':
        return ['name', 'deanName', 'principalDeanName'];
      case 'campus':
        return ['name', 'campusName'];
      default:
        return [];
    }
  }

  private bindStateOptions(): void {
    this.stateOptions = this.stateFilter.valueChanges.pipe(
      startWith(''),
      map((value: string) => this.filterList(value, this.states, ['name', 'stateName']))
    );
  }

  private bindCityOptions(): void {
    this.cityOptions = this.cityFilter.valueChanges.pipe(
      startWith(''),
      map((value: string) => this.filterList(value, this.cities, ['name', 'cityName']))
    );
  }

  private bindZoneOptions(): void {
    this.zoneOptions = this.zoneFilter.valueChanges.pipe(
      startWith(''),
      map((value: string) => this.filterList(value, this.zones, ['zoneName', 'zone']))
    );
  }

  private bindCampusOptions(): void {
    this.campusOptions = this.campusFilter.valueChanges.pipe(
      startWith(''),
      map((value: string) => this.filterList(value, this.campuses, ['name', 'campusName']))
    );
  }

  private resetAllSelections(): void {
    this.customizeRows = [];
    this.customizeRowId = 0;

    this.selectedLos = null;
    this.selectedState = null;
    this.selectedCity = null;
    this.selectedZone = null;
    this.selectedSubZone = null;
    this.selectedDean = null;
    this.selectedCampus = null;

    this.losList = [];
    this.states = [];
    this.cities = [];
    this.zones = [];
    this.subZones = [];
    this.deans = [];
    this.campuses = [];

    this.losFilter.setValue('');
    this.stateFilter.setValue('');
    this.cityFilter.setValue('');
    this.zoneFilter.setValue('');
    this.subZoneFilter.setValue('');
    this.deanFilter.setValue('');
    this.campusFilter.setValue('');

    this.appliedLos = '';
    this.appliedStateId = null;
    this.appliedCityId = null;
    this.appliedZoneName = '';
    this.appliedSubZone = '';
    this.appliedDean = '';
  }

  private subscribeFilterApi(
    request$: Observable<any>,
    apply: (list: any[]) => void,
    options: { showSpinner?: boolean; onNoData?: () => void } = {}
  ): void {
    const { showSpinner = false, onNoData } = options;

    if (showSpinner) {
      this.spinner.show();
    }

    request$
      .pipe(finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }))
      .subscribe({
        next: (res: any) => {
          if (this.isFilterNoContent(res)) {
            this.showFilterNoDataAlert();
            onNoData?.();
            apply([]);
            return;
          }

          const list = this.extractList(res);
          if (!list.length) {
            this.showFilterNoDataAlert();
            onNoData?.();
          }
          apply(list);
        },
        error: (err) => this.handleFilterApiError(err, () => {
          onNoData?.();
          apply([]);
        }),
      });
  }

  private handleFilterApiError(err: unknown, onNoData?: () => void): void {
    if ((err as HttpErrorResponse)?.status === 204) {
      this.showFilterNoDataAlert();
      onNoData?.();
      return;
    }
    console.error(err);
    onNoData?.();
  }

  private isFilterNoContent(res: unknown): boolean {
    return res === null || res === undefined;
  }

  private showFilterNoDataAlert(): void {
    this.hrAlert.show(this.filterNoDataMessage, 'Warning');
  }

  private usesDashboardState(): boolean {
    return !this.dialogData?.skipDashboardState;
  }

  private persistFilterDialogState(): void {
    if (!this.usesDashboardState()) {
      return;
    }
    this.dashboardState.setFilterDialogState(this.buildFilterDialogState());
  }

  private buildFilterDialogState(): MrfFilterDialogState {
    return {
      filterMode: this.filterMode,
      selectedLos: this.selectedLos,
      selectedState: this.selectedState,
      selectedCity: this.selectedCity,
      selectedZone: this.selectedZone,
      selectedSubZone: this.selectedSubZone,
      selectedDean: this.selectedDean,
      selectedCampus: this.selectedCampus,
      losList: [...this.losList],
      states: [...this.states],
      cities: [...this.cities],
      zones: [...this.zones],
      subZones: [...this.subZones],
      deans: [...this.deans],
      campuses: [...this.campuses],
      searchTexts: {
        los: this.losFilter.value,
        state: this.stateFilter.value,
        city: this.cityFilter.value,
        zone: this.zoneFilter.value,
        subZone: this.subZoneFilter.value,
        dean: this.deanFilter.value,
        campus: this.campusFilter.value,
      },
      appliedLos: this.appliedLos,
      appliedStateId: this.appliedStateId,
      appliedCityId: this.appliedCityId,
      appliedZoneName: this.appliedZoneName,
      appliedSubZone: this.appliedSubZone,
      appliedDean: this.appliedDean,
      customizeRows: this.customizeRows.map((row) => ({
        id: row.id,
        filterBy: row.filterBy,
        selectedValue: row.selectedValue,
        options: [...row.options],
        searchText: row.searchControl.value,
      })),
      customizeRowId: this.customizeRowId,
    };
  }

  private restoreFilterDialogState(state: MrfFilterDialogState): void {
    this.filterMode = this.dialogData?.mode === 'customize' ? 'customize' : 'default';
    this.selectedLos = state.selectedLos;
    this.selectedState = state.selectedState;
    this.selectedCity = state.selectedCity;
    this.selectedZone = state.selectedZone;
    this.selectedSubZone = state.selectedSubZone;
    this.selectedDean = state.selectedDean;
    this.selectedCampus = state.selectedCampus;

    this.losList = [...(state.losList ?? [])];
    this.states = [...(state.states ?? [])];
    this.cities = [...(state.cities ?? [])];
    this.zones = [...(state.zones ?? [])];
    this.subZones = [...(state.subZones ?? [])];
    this.deans = [...(state.deans ?? [])];
    this.campuses = [...(state.campuses ?? [])];

    this.losFilter.setValue(state.searchTexts?.los ?? '');
    this.stateFilter.setValue(state.searchTexts?.state ?? '');
    this.cityFilter.setValue(state.searchTexts?.city ?? '');
    this.zoneFilter.setValue(state.searchTexts?.zone ?? '');
    this.subZoneFilter.setValue(state.searchTexts?.subZone ?? '');
    this.deanFilter.setValue(state.searchTexts?.dean ?? '');
    this.campusFilter.setValue(state.searchTexts?.campus ?? '');

    this.appliedLos = state.appliedLos ?? '';
    this.appliedStateId = state.appliedStateId ?? null;
    this.appliedCityId = state.appliedCityId ?? null;
    this.appliedZoneName = state.appliedZoneName ?? '';
    this.appliedSubZone = state.appliedSubZone ?? '';
    this.appliedDean = state.appliedDean ?? '';

    this.customizeRowId = state.customizeRowId ?? 0;
    this.customizeRows = (state.customizeRows ?? []).map((row) => {
      const searchControl = new FormControl(row.searchText ?? '', { nonNullable: true });
      const restoredRow: CustomizeFilterRow = {
        id: row.id,
        filterBy: row.filterBy,
        selectedValue: row.selectedValue,
        options: [...(row.options ?? [])],
        searchControl,
        filteredOptions$: of([]),
        loading: false,
      };
      this.bindCustomizeRowOptions(restoredRow);
      return restoredRow;
    });

    if (this.isCustomizeMode && !this.customizeRows.length) {
      this.initCustomizeRows();
    }

    this.bindDefaultOptionStreams();

    if (!this.losList.length && this.isDefaultMode) {
      this.loadLos();
    }
  }

  private bindDefaultOptionStreams(): void {
    if (this.losList.length) {
      this.losOptions = this.losFilter.valueChanges.pipe(
        startWith(this.losFilter.value),
        map((value: string) => this.filterList(value, this.losList, ['name', 'location', 'los', 'losName']))
      );
    }

    if (this.states.length) {
      this.bindStateOptions();
    }

    if (this.cities.length) {
      this.bindCityOptions();
    }

    if (this.zones.length) {
      this.bindZoneOptions();
    }

    if (this.subZones.length) {
      this.subZoneOptions = this.subZoneFilter.valueChanges.pipe(
        startWith(this.subZoneFilter.value),
        map((value: string) =>
          this.filterList(value, this.subZones, ['name', 'subZone', 'subZoneName'])
        )
      );
    }

    if (this.deans.length) {
      this.deanOptions = this.deanFilter.valueChanges.pipe(
        startWith(this.deanFilter.value),
        map((value: string) =>
          this.filterList(value, this.deans, ['name', 'deanName', 'principalDeanName'])
        )
      );
    }

    if (this.campuses.length) {
      this.bindCampusOptions();
    }
  }

  private extractList(res: any): any[] {
    return Array.isArray(res) ? res : (res?.data ?? []);
  }

  private filterList(value: string, list: any[], fields: string[]): any[] {
    const search = (value ?? '').trim().toLowerCase();
    return list.filter((item) =>
      fields.some((field) =>
      item?.[field]?.toString().trim().toLowerCase().includes(search)
      )
    );
  }

  private getLosValue(item: any): string {
    return String(item?.name ?? item?.location ?? item?.los ?? item?.losName ?? '').trim();
  }

  private getSubZoneValue(item: any): string {
    return String(item?.name ?? item?.subZone ?? item?.subZoneName ?? '').trim();
  }

  private getDeanValue(item: any): string {
    return String(item?.name ?? item?.deanName ?? item?.principalDeanName ?? '').trim();
  }

  private clearLosDependents(): void {
    this.appliedStateId = null;
    this.appliedCityId = null;
    this.appliedZoneName = '';
    this.appliedSubZone = '';
    this.appliedDean = '';
    this.clearDefaultState();
  }

  private clearStateDependents(): void {
    this.appliedCityId = null;
    this.appliedZoneName = '';
    this.appliedSubZone = '';
    this.appliedDean = '';
    this.clearDefaultCity();
  }

  private clearCityDependents(): void {
    this.appliedZoneName = '';
    this.appliedSubZone = '';
    this.appliedDean = '';
    this.clearDefaultZone();
  }

  private clearZoneDependents(): void {
    this.appliedSubZone = '';
    this.appliedDean = '';
    this.clearSubZone();
  }

  private clearSubZoneDependents(): void {
    this.appliedDean = '';
    this.clearDean();
  }

  private clearDeanDependents(): void {
    this.clearCampus();
  }

  private resetCustomizeRowSelection(row: CustomizeFilterRow): void {
    row.selectedValue = null;
    row.options = [];
    row.searchControl.setValue('');
    this.bindCustomizeRowOptions(row);
  }

  private clearCustomizeDependents(changedRow: CustomizeFilterRow): void {
    const changedIndex = this.customizeRows.findIndex((row) => row.id === changedRow.id);
    if (changedIndex === -1) {
      return;
    }

    for (let i = changedIndex + 1; i < this.customizeRows.length; i++) {
      this.resetCustomizeRowSelection(this.customizeRows[i]);
    }
  }

  private reloadCustomizeDependents(changedRow: CustomizeFilterRow): void {
    const changedIndex = this.customizeRows.findIndex((row) => row.id === changedRow.id);
    if (changedIndex === -1) {
      return;
    }

    for (let i = changedIndex + 1; i < this.customizeRows.length; i++) {
      const row = this.customizeRows[i];
      if (row.filterBy) {
        this.loadCustomizeRowOptions(row);
      }
    }
  }

  private isSameFilterValue(
    filterBy: MrfFilterByField,
    previous: any | null,
    next: any | null
  ): boolean {
    if (!previous || !next) {
      return false;
    }

    switch (filterBy) {
      case 'los':
        return this.getLosValue(previous) === this.getLosValue(next);
      case 'state':
        return previous.stateId === next.stateId;
      case 'city':
        return previous.cityId === next.cityId;
      case 'zone':
        return previous.zoneName === next.zoneName;
      case 'subZone':
        return this.getSubZoneValue(previous) === this.getSubZoneValue(next);
      case 'deanName':
        return this.getDeanValue(previous) === this.getDeanValue(next);
      case 'campus':
        return previous.campusId === next.campusId;
      default:
        return false;
    }
  }

  private clearCampus(): void {
    this.selectedCampus = null;
    this.campuses = [];
    this.campusFilter.setValue('');
  }

  private clearDean(): void {
    this.selectedDean = null;
    this.deans = [];
    this.deanFilter.setValue('');
    this.clearCampus();
  }

  private clearSubZone(): void {
    this.selectedSubZone = null;
    this.subZones = [];
    this.subZoneFilter.setValue('');
    this.clearDean();
  }

  private clearDefaultZone(): void {
    this.selectedZone = null;
    this.zones = [];
    this.zoneFilter.setValue('');
    this.clearSubZone();
  }

  private clearDefaultCity(): void {
    this.selectedCity = null;
    this.cities = [];
    this.cityFilter.setValue('');
    this.clearDefaultZone();
  }

  private clearDefaultState(): void {
    this.selectedState = null;
    this.states = [];
    this.stateFilter.setValue('');
    this.clearDefaultCity();
  }
}
